import { gymLocalDate } from '../../lib/gymLocalDate'

/** A renewal queued behind the plan being edited. */
export interface UpcomingPlan {
  plan_name: string | null
  starts_at: string
  expires_at: string | null
}

/** Gym-local days, as YYYY-MM-DD. */
export interface DayRange {
  start: string
  end: string | null
}

export interface RenewalMovePreview {
  from: DayRange
  to: DayRange
  /** The plan now ends before today, so the member holds no plan today. */
  gapToday: boolean
}

function addDays(day: string, days: number): string {
  const d = new Date(`${day}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

function daysBetween(from: string, to: string): number {
  const ms = new Date(`${to}T12:00:00Z`).getTime() - new Date(`${from}T12:00:00Z`).getTime()
  return Math.round(ms / 86_400_000)
}

/**
 * Where a queued renewal lands if the current plan is saved this way. Mirrors
 * `renewalStartFor` and `realignRenewal` in the Worker: the day after the new
 * last day, never earlier than tomorrow, the whole window moved by the same
 * number of days. A renewal that has already started is never moved.
 *
 * `ending` is `'ended'` when the plan is being cancelled or used up, which
 * sends the renewal to tomorrow; otherwise it is the new last day.
 *
 * Only a preview: the toast after saving shows the dates the server set.
 */
export function previewRenewalMove(
  renewal: UpcomingPlan,
  ending: string | 'ended',
  timezone: string | null,
  now: Date = new Date(),
): RenewalMovePreview | null {
  if (new Date(renewal.starts_at) <= now) return null
  const today = gymLocalDate(now, timezone)
  const tomorrow = addDays(today, 1)

  let start = tomorrow
  if (ending !== 'ended') {
    const dayAfter = addDays(ending, 1)
    if (dayAfter > tomorrow) start = dayAfter
  }

  const fromStart = gymLocalDate(new Date(renewal.starts_at), timezone)
  if (start === fromStart) return null
  const fromEnd = renewal.expires_at ? gymLocalDate(new Date(renewal.expires_at), timezone) : null
  const shift = daysBetween(fromStart, start)

  return {
    from: { start: fromStart, end: fromEnd },
    to: { start, end: fromEnd ? addDays(fromEnd, shift) : null },
    gapToday: ending !== 'ended' && ending < today,
  }
}

/**
 * A gym-local day for display, in the app's language rather than the
 * browser's, day first like the date fields: "26.10.2026" in Romanian,
 * "26/10/2026" in English.
 */
export function formatDay(day: string, language: string): string {
  return new Date(`${day}T12:00:00Z`).toLocaleDateString(
    language.startsWith('ro') ? 'ro-RO' : 'en-GB',
    { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' },
  )
}
